import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import CircularProgress from "@material-ui/core/CircularProgress";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";
import InfoIcon from "@material-ui/icons/Info";
import A_IconButton from "../../../Atoms/Inputs/Buttons/A_IconButton";
import ConfirmDialog from "../../../Molecules/Common/Provision/ConfirmDialog";
import { toDisplayDate } from "../../../../../domain/provision/provision.rules";
import { api } from "./Provision.helper";
import useStyles from "./Provision.styles";

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "-";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Uploaded DCF Files (claude-code-UI-prompt.md §Screen structure 5, DFXX11): File Name, File
 * Type, Uploaded By, Uploaded On, Actions — click name to open; download/delete from Actions.
 */
const UploadedDcfFilesTable = ({ loanId, documents, editable, onDeleted }) => {
  const classes = useStyles();
  // Opening a file goes direct to the Document Helper (our API is not a download proxy), and that
  // helper carries its own bearer token — hence the explicit oidc user, as in QuotesEditor.jsx.
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState({ open: false });

  const handleOpen = async (document) => {
    setBusyId(document.id);
    setError(null);
    try {
      // Resolves having already triggered the browser save — there is no blob to handle here.
      await api.downloadDcfDocument(document, oidcUser);
    } catch (err) {
      setError("Couldn't open the file. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const requestDelete = (document) => {
    setConfirm({
      open: true,
      title: "Delete DCF file",
      message: (
        <>
          Delete <b>{document.fileName}</b>? This cannot be undone.
        </>
      ),
      onConfirm: async () => {
        setBusyId(document.id);
        setConfirm({ open: false });
        try {
          await api.deleteDcfDocument(loanId, document.id);
          onDeleted(document.id);
        } catch (err) {
          setError(err.message || "Delete failed. Please try again.");
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  return (
    <div className={classes.card} style={{ marginBottom: 0 }}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderTitle}>Uploaded DCF Files</span>
      </div>
      <div className={classes.cardBody}>
        <p className={classes.subtle} style={{ marginTop: 0 }}>
          Files uploaded against this provision.
        </p>
        {error && (
          <div className={classes.validationSummary} style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}
        {documents.length === 0 ? (
          <div className={classes.subtle}>No files uploaded yet.</div>
        ) : (
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Uploaded On</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document) => (
                <TableRow key={document.id}>
                  <TableCell>
                    <span
                      className={classes.linkAction}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpen(document)}
                      onKeyPress={(event) => event.key === "Enter" && handleOpen(document)}
                    >
                      {document.fileName}
                    </span>
                  </TableCell>
                  <TableCell className={classes.subtle}>{document.fileType}</TableCell>
                  <TableCell className={classes.subtle}>{document.uploadedBy}</TableCell>
                  <TableCell className={classes.subtle}>{toDisplayDate(document.uploadedOn)}</TableCell>
                  <TableCell className={classes.subtle}>{formatSize(document.sizeBytes)}</TableCell>
                  <TableCell align="right">
                    {busyId === document.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <>
                        <A_IconButton
                          size="small"
                          icon={<GetAppIcon fontSize="small" />}
                          ariaLabel={`Download ${document.fileName}`}
                          onClick={() => handleOpen(document)}
                        />
                        <A_SecuredAction
                          permission={pageSecurityActions.DELETE}
                        >
                          <A_IconButton
                            size="small"
                            icon={<DeleteIcon fontSize="small" />}
                            ariaLabel={`Delete ${document.fileName}`}
                            onClick={() => requestDelete(document)}
                            disabled={!editable}
                          />
                        </A_SecuredAction>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className={classes.hint} style={{ marginTop: 10 }}>
          <InfoIcon fontSize="inherit" /> Click a file name to open · download or delete from Actions.
        </div>
      </div>

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onConfirm={confirm.onConfirm}
        onClose={() => setConfirm({ open: false })}
      />
    </div>
  );
};

UploadedDcfFilesTable.propTypes = {
  loanId: PropTypes.string.isRequired,
  documents: PropTypes.array.isRequired,
  editable: PropTypes.bool,
  onDeleted: PropTypes.func.isRequired,
};

export default UploadedDcfFilesTable;
